/**
 * phaseModel — the ONLY module that reads the server's phase-model key names.
 *
 * `run-phase-model` publishes three top-level status keys (`phase_model`,
 * `chronology`, `evidence_decision`) plus a grouped catalog manifest
 * (`phases`, `milestones`, `exempt_step_ids`). Every component in this feature
 * receives a NORMALIZED view model from here and never touches a wire key, so
 * a substrate rename changes exactly one file.
 *
 * Everything here is PURE: no React, no transport, no `Date.now()`, no
 * `Office.*`. It runs in jsdom where `Office` is undefined.
 *
 * Deliberately NOT replaced by this module: `phaseStatus.ts`'s
 * `derivePhaseStatusesFromInstance`. That stays as the PRE-MODEL fallback and
 * the flag-off path (design Constraint 14).
 *
 * Honesty rules encoded here, each of which has a spec case:
 *   - display names are the server's strings VERBATIM; a null display name
 *     renders the id plus an explicit "(name not reported)" marker, never a
 *     prettified invention;
 *   - `source` has THREE outcomes (model / pre_model / undeclared) and they are
 *     never collapsed into two;
 *   - no step id and no step display name reaches the stream model except
 *     `legacyStepLabel`, which is only populated when `source !== "model"`;
 *   - a terminal-not-completed run never paints an unvisited phase `completed`
 *     (the "cancelled run painting every step green" regression);
 *   - parentage is read from the DECLARED manifest; ORDER is never derived
 *     here (the chronology's array order is the server's, see `readChronology`).
 */

import type { EvidencePresentation } from "./carrierEvidenceCopy";
import { emitDrawerTelemetry } from "./drawerTelemetry";
// The ONE declaration of the v2 mortgagee workflow id. Imported rather than
// re-spelled so `carrierEvidenceCandidateFor` cannot drift from the launch
// surface the way the two hand-copied host conditionals it replaces did.
import { V15_MORTGAGEE_WORKFLOW_ID } from "./launch/launchGuards";
import { isWaitingOnPerson } from "./statusPill";
import type {
  ChronologyEntry,
  EvidenceDecisionSnapshot,
  InstanceStatusSnapshot,
  ManifestMilestone,
  ManifestPhase,
} from "./types";

// ---------------------------------------------------------------------------
// View models
// ---------------------------------------------------------------------------

export type PhaseRowState =
  | "completed"
  | "running"
  | "hitl_waiting"
  | "failed"
  | "not_reached"
  | "unknown";

export interface MilestoneRow {
  id: string;
  label: string;
  state: PhaseRowState;
  current: boolean;
}

export interface PhaseRow {
  id: string;
  label: string;
  state: PhaseRowState;
  current: boolean;
  milestones: MilestoneRow[];
}

export interface PhaseStreamModel {
  /** "model" = the server published a phase model; "pre_model" = this instance
   *  predates it; "undeclared" = the workflow registers no manifest. */
  source: "model" | "pre_model" | "undeclared";
  /** Verbatim server display name (or `<id> (name not reported)`). */
  phaseLabel: string | null;
  /** Verbatim server display name (or `<id> (name not reported)`). */
  milestoneLabel: string | null;
  /** The pointer sits on the exempt Epic login spine: no phase, no milestone.
   *  This is "in the login sequence", NOT "not started". */
  stepExempt: boolean;
  /** WHICH sign-in state to describe while `stepExempt`; `null` otherwise.
   *
   *  - `"first"`   — the run is signing in before any work has started.
   *  - `"again"`   — at least one phase is already behind us, so the run is
   *                  re-establishing the browser mid-flight (a cold resume, a
   *                  dead page, a re-entry). Worth distinguishing: the same
   *                  words twenty minutes in read as if nothing has happened.
   *  - `"awaiting_mfa"` — blocked on a human approving the sign-in challenge.
   *                  The USER is the blocker here, so the card must say so.
   *
   *  Deliberately a state, not the step id: this module's contract is that no
   *  step id and no step display name reaches the stream model except
   *  `legacyStepLabel`. The view maps this to copy. */
  signInState: "first" | "again" | "awaiting_mfa" | null;
  /** MAY accompany the two names — never replaces them (AC-1). */
  counter: { index: number; total: number } | null;
  rows: PhaseRow[];
  /** Legacy flat step label, populated ONLY when `source !== "model"`. */
  legacyStepLabel: string | null;
}

/** The grouped catalog manifest, in the shape either host's catalog entry
 *  carries it. Passing `null` is legitimate: an ungrouped workflow OMITS
 *  these keys entirely. */
export interface PhaseManifest {
  phases?: ManifestPhase[] | null;
  milestones?: ManifestMilestone[] | null;
  exempt_step_ids?: string[] | null;
}

/** A snapshot as the renderer sees it — the server shape plus the ONE
 *  client-only staleness marker the live push fold writes. */
type StreamSnapshot = InstanceStatusSnapshot & {
  __phase_model_stale?: boolean;
};

// ---------------------------------------------------------------------------
// Status vocabulary (local, so the adapter never imports a host)
// ---------------------------------------------------------------------------

const TERMINAL_NOT_COMPLETED = new Set([
  "cancelled",
  "failed",
  "rejected",
  "timeout_cancelled",
]);

const HITL_WAITING = new Set([
  "paused_hitl",
  "warm_hitl",
  "rpa_review",
  "pending_edocs",
  "pending_email_send",
]);

/** Rendered when the server carried an id but no display name. Explicit by
 *  design — an invented title-cased label would read as a real server string. */
export const NAME_NOT_REPORTED_SUFFIX = "(name not reported)";

function labelFor(id: string, displayName: string | null | undefined): string {
  const name = typeof displayName === "string" ? displayName : "";
  return name !== "" ? name : `${id} ${NAME_NOT_REPORTED_SUFFIX}`;
}

// ---------------------------------------------------------------------------
// Declared parentage
// ---------------------------------------------------------------------------

/**
 * Re-resolve the (phase, milestone) pointer for a step id using ONLY the
 * declared manifest parentage — used when a push delta carries a step id but
 * no `phase_model` (AD-PLAN-4). Returns null for an id the manifest does not
 * declare, so an unmanifested step can never be given an invented parent.
 */
export function resolvePointerForStep(
  stepId: string | null | undefined,
  manifest: PhaseManifest | null | undefined,
): { phaseId: string; milestoneId: string } | null {
  if (!stepId || !manifest?.milestones) return null;
  for (const m of manifest.milestones) {
    if (!m?.step_ids) continue;
    if (m.step_ids.includes(stepId)) {
      return { phaseId: m.phase_id, milestoneId: m.milestone_id };
    }
  }
  return null;
}

function isExemptStep(
  stepId: string | null | undefined,
  manifest: PhaseManifest | null | undefined,
): boolean {
  if (!stepId || !manifest?.exempt_step_ids) return false;
  return manifest.exempt_step_ids.includes(stepId);
}

/**
 * Belongs to the Epic login spine — the DECLARED exempt list, plus the steps
 * this module already knows are a person-blocked sign-in hold
 * ({@link AWAITING_PERSON_STEP_IDS}).
 *
 * The second half matters only when the two disagree: a registered manifest
 * that has not caught up with the graph does not declare the warm hold exempt,
 * and reading that omission as "the run left the sign-in" is how the honest
 * "waiting for you to approve the sign-in" line turns into a generic working
 * message. Used ONLY to decide whether the run has LEFT the spine — never to
 * put it there, which would be inventing an exempt state the server never
 * published.
 */
function isSpineStep(
  stepId: string | null | undefined,
  manifest: PhaseManifest | null | undefined,
): boolean {
  if (!stepId) return false;
  return isExemptStep(stepId, manifest) || AWAITING_PERSON_STEP_IDS.has(stepId);
}

// ---------------------------------------------------------------------------
// buildPhaseStream
// ---------------------------------------------------------------------------

function emitFallback(
  snapshot: StreamSnapshot | null,
  cause: "pre_model" | "undeclared",
): void {
  const ids = snapshot as
    | { instance_id?: string | null; workflow_id?: string | null }
    | null;
  emitDrawerTelemetry("emuw_phase_model_fallback", {
    instanceId: ids?.instance_id ?? null,
    workflowId: ids?.workflow_id ?? null,
    detail: cause,
  });
}

function emptyModel(
  source: PhaseStreamModel["source"],
  legacyStepLabel: string | null,
  counter: PhaseStreamModel["counter"],
): PhaseStreamModel {
  return {
    source,
    phaseLabel: null,
    milestoneLabel: null,
    stepExempt: false,
    // Not on the login spine by construction (`stepExempt: false`), so there
    // is no sign-in state to describe.
    signInState: null,
    counter,
    rows: [],
    legacyStepLabel,
  };
}

function readCounter(
  snapshot: StreamSnapshot | null,
): PhaseStreamModel["counter"] {
  const index = snapshot?.step_index;
  const total = snapshot?.total_steps;
  if (index == null || total == null || total <= 0) return null;
  return { index, total };
}

function legacyLabel(snapshot: StreamSnapshot | null): string | null {
  if (!snapshot) return null;
  return (
    snapshot.current_step_display_name ??
    snapshot.step_id ??
    snapshot.current_phase ??
    null
  );
}

/**
 * Positive evidence that a phase / milestone actually ran, taken ONLY from
 * declared server data: the ordered `chronology` and the `phases[]` history.
 * Absence of evidence is never turned into `completed`.
 */
function collectVisited(snapshot: StreamSnapshot | null): {
  phases: Set<string>;
  milestones: Set<string>;
} {
  const phases = new Set<string>();
  const milestones = new Set<string>();
  const chronology: ChronologyEntry[] = snapshot?.chronology ?? [];
  for (const entry of chronology) {
    if (entry?.phase_id) phases.add(entry.phase_id);
    if (entry?.milestone_id) milestones.add(entry.milestone_id);
  }
  for (const row of snapshot?.phases ?? []) {
    if (row?.phase_id) phases.add(row.phase_id);
  }
  return { phases, milestones };
}

/** The sign-in steps that are genuinely blocked on a person, listed rather
 *  than matched on the `epic_mfa` prefix.
 *
 *  The prefix is NOT a proxy for "a human is needed". `epic_mfa_submit_code`
 *  is the automated service-account path (a TOTP code the run fetches and
 *  submits itself), and `epic_mfa_method_choice` / `epic_mfa_code_entry` ask
 *  the reader to CHOOSE a method or TYPE a code, which the "approve" wording
 *  does not describe. Only the warm hold is a push waiting to be approved.
 *
 *  Today the workflow's exempt-step list contains exactly one of these, so a
 *  new one reaching this card falls back to the plain "Signing in to Epic".
 *  That is the right way to be wrong: always true, never a false claim that
 *  the reader is holding the run up. */
const AWAITING_PERSON_STEP_IDS = new Set(["epic_mfa_warm_hold"]);

export function resolveSignInState(
  stepId: string | null,
  anyPhaseVisited: boolean,
): "first" | "again" | "awaiting_mfa" {
  if (stepId !== null && AWAITING_PERSON_STEP_IDS.has(stepId)) {
    return "awaiting_mfa";
  }
  return anyPhaseVisited ? "again" : "first";
}

// ---------------------------------------------------------------------------
// Who supplies the carrier's confirmation (story 28633)
// ---------------------------------------------------------------------------

/** The document milestone's id in the v2 manifest. */
export const DOCUMENTS_MILESTONE_ID = "awaiting_carrier_edocs";
/** Its name when the AM, not the carrier, supplies the confirmation. The
 *  carrier case keeps the manifest's own name ("Awaiting carrier eDocs"). */
export const AM_DOCUMENTS_MILESTONE_LABEL = "Waiting for your documents";

function readObj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

/**
 * Does the AM supply the carrier's confirmation on this run? `true` / `false`
 * when the snapshot says, `null` when nothing does yet. Most specific first:
 *   1. the parked question's `evidence_source` — follows a take-over / revert;
 *   2. the recorded evidence decision's branch (Stage 4 onward);
 *   3. the carrier's capability, recorded on the run when ora resolves it
 *      (`metadata.carrier_capability.has_edocs`, Stage 2) — no eDocs means the
 *      AM supplies.
 */
export function amSuppliesDocuments(snapshot: StreamSnapshot | null): boolean | null {
  if (!snapshot) return null;
  const source = readObj(snapshot.hitl_question)?.evidence_source;
  if (source === "am") return true;
  if (source === "carrier") return false;
  const branch = snapshot.evidence_decision?.branch;
  if (branch === "am_supplies") return true;
  if (branch === "carrier_supplies") return false;
  const capability = readObj(
    readObj((snapshot as { metadata?: unknown }).metadata)?.carrier_capability,
  );
  if (typeof capability?.has_edocs === "boolean") return !capability.has_edocs;
  return null;
}

/** A milestone's name for THIS run: the manifest's, except the document
 *  milestone when the AM supplies the documents. */
function milestoneLabelForRun(
  milestoneId: string,
  displayName: string | null | undefined,
  amSupplies: boolean | null,
): string {
  if (milestoneId === DOCUMENTS_MILESTONE_ID && amSupplies === true) {
    return AM_DOCUMENTS_MILESTONE_LABEL;
  }
  return labelFor(milestoneId, displayName);
}

function pointerPhaseState(status: string): PhaseRowState {
  if (status === "failed") return "failed";
  if (HITL_WAITING.has(status)) return "hitl_waiting";
  if (status === "completed") return "completed";
  // cancelled / rejected / timeout_cancelled: the run stopped here, and there
  // is no positive evidence this phase finished. "unknown" is the honest
  // answer; "completed" is the regression this rule exists to prevent.
  if (TERMINAL_NOT_COMPLETED.has(status)) return "unknown";
  return "running";
}

export function buildPhaseStream(
  snapshot: StreamSnapshot | null,
  manifest: PhaseManifest | null | undefined,
): PhaseStreamModel {
  const counter = readCounter(snapshot);
  const pointer = snapshot?.phase_model;

  // Three distinct outcomes — never collapsed into two. Each fallback is
  // RECORDED once per (instance, cause) so the rate is greppable rather than
  // guessed; the emitter de-duplicates, so a render loop cannot turn one
  // fallback into a stream.
  if (pointer == null) {
    emitFallback(snapshot, "pre_model");
    return emptyModel("pre_model", legacyLabel(snapshot), counter);
  }
  if (pointer.declared === false) {
    emitFallback(snapshot, "undeclared");
    return emptyModel("undeclared", legacyLabel(snapshot), counter);
  }

  const status = (snapshot?.status ?? "").toLowerCase();
  const visited = collectVisited(snapshot);

  // AD-PLAN-4: a push delta that carried only a step_id marks the pointer
  // stale. Re-resolve through the DECLARED manifest parentage — never by a
  // positional guess, and never by re-ordering anything.
  let pointerPhaseId = pointer.phase?.id ?? null;
  let pointerMilestoneId = pointer.milestone?.id ?? null;
  let stepExempt = pointer.step_exempt === true;

  if (snapshot?.__phase_model_stale) {
    const stepId = snapshot.step_id ?? null;
    if (isExemptStep(stepId, manifest)) {
      pointerPhaseId = null;
      pointerMilestoneId = null;
      stepExempt = true;
    } else {
      const resolved = resolvePointerForStep(stepId, manifest);
      if (resolved) {
        pointerPhaseId = resolved.phaseId;
        pointerMilestoneId = resolved.milestoneId;
        stepExempt = false;
      }
      // Unresolvable id: keep the server's last pointer rather than invent one.
    }
  }

  // Push events keep the last phase_model block (often the login spine). Once
  // the live step_id has left the exempt list, never keep showing the sign-in
  // card with the All-phases rail hidden.
  const liveStepId = snapshot?.step_id ?? pointer.step?.id ?? null;
  // Held for the sign-in state below — read BEFORE the block that may clear
  // `stepExempt`, because that block is about leaving the spine, not about
  // which spine state we were in.
  const exemptStepId = liveStepId;
  if (liveStepId && !isSpineStep(liveStepId, manifest)) {
    const resolved = resolvePointerForStep(liveStepId, manifest);
    if (resolved) {
      if (stepExempt) {
        pointerPhaseId = resolved.phaseId;
        pointerMilestoneId = resolved.milestoneId;
      }
      // POSITIVE evidence the run has left the spine: the manifest declares
      // this step under a milestone, so it is work, not sign-in.
      stepExempt = false;
    }
    // Unresolvable id — the manifest declares it NOWHERE, neither as an exempt
    // login step nor under a milestone. That is manifest/graph SKEW, not
    // evidence the run left the spine, and clearing `stepExempt` on it threw
    // away the only honest thing the card had to say: `signInState` collapsed
    // to null and "you are being asked to approve the sign-in" degraded to a
    // generic "Working in Epic" (review finding #24). The pointer above is
    // already kept for exactly this case ("keep the server's last pointer
    // rather than invent one"); the exempt flag gets the same treatment, so an
    // undeclared step id can never SILENTLY discard a true awaiting-person
    // signal. An id the manifest does declare still clears it, which is the
    // stuck-sign-in-card regression this block was written for.
  }

  const manifestPhases = manifest?.phases ?? [];
  const manifestMilestones = manifest?.milestones ?? [];

  // Never "current" BEHIND work the history proves already ran (story 28633).
  // The pointer is the run's saved step, and a saved step can lag the history
  // (a mid-run Epic re-sign-in has left it on the carrier step while the run
  // drafted the reply and applied the additional interest). Milestones are in
  // execution order, so the furthest one with positive evidence (chronology)
  // is at least as current as the pointer: move the marker forward to it,
  // never backward. Not on the sign-in spine, where there is no milestone.
  if (!stepExempt && pointerMilestoneId != null) {
    const pointerMilestoneIndex = manifestMilestones.findIndex(
      (m) => m.milestone_id === pointerMilestoneId,
    );
    let furthestVisited = -1;
    manifestMilestones.forEach((m, i) => {
      if (visited.milestones.has(m.milestone_id)) furthestVisited = i;
    });
    if (pointerMilestoneIndex >= 0 && furthestVisited > pointerMilestoneIndex) {
      const ahead = manifestMilestones[furthestVisited];
      pointerMilestoneId = ahead.milestone_id;
      pointerPhaseId = ahead.phase_id;
    }
  }

  const amSupplies = amSuppliesDocuments(snapshot);

  const pointerIndex = manifestPhases.findIndex(
    (p) => p.phase_id === pointerPhaseId,
  );

  const rows: PhaseRow[] = manifestPhases.map((p, i) => {
    const isCurrent = pointerPhaseId != null && p.phase_id === pointerPhaseId;
    let state: PhaseRowState;
    if (isCurrent) {
      state = pointerPhaseState(status);
    } else if (visited.phases.has(p.phase_id)) {
      state = "completed";
    } else if (pointerIndex >= 0 && i > pointerIndex) {
      state = "not_reached";
    } else if (pointerIndex < 0) {
      // Nothing has entered a phase yet (or the pointer sits on the exempt
      // login spine): every declared phase is genuinely not reached.
      state = "not_reached";
    } else {
      // Below the pointer with NO positive evidence. Never `completed`.
      state = TERMINAL_NOT_COMPLETED.has(status) ? "not_reached" : "unknown";
    }

    const milestones: MilestoneRow[] = manifestMilestones
      .filter((m) => m.phase_id === p.phase_id)
      .map((m) => {
        const mCurrent =
          isCurrent &&
          pointerMilestoneId != null &&
          m.milestone_id === pointerMilestoneId;
        let mState: PhaseRowState;
        if (mCurrent) {
          mState = state;
        } else if (visited.milestones.has(m.milestone_id)) {
          mState = "completed";
        } else if (state === "not_reached") {
          mState = "not_reached";
        } else if (state === "completed") {
          // The phase finished but this milestone left no row behind — say so
          // rather than claim it ran.
          mState = "unknown";
        } else {
          mState = TERMINAL_NOT_COMPLETED.has(status)
            ? "not_reached"
            : "unknown";
        }
        return {
          id: m.milestone_id,
          label: milestoneLabelForRun(m.milestone_id, m.name, amSupplies),
          state: mState,
          current: mCurrent,
        };
      });

    return {
      id: p.phase_id,
      label: labelFor(p.phase_id, p.name),
      state,
      current: isCurrent,
      milestones,
    };
  });

  // Labels: prefer the server's own pointer strings; fall back to the declared
  // manifest name when the pointer was re-resolved from a stale push delta.
  const phaseFromManifest = manifestPhases.find(
    (p) => p.phase_id === pointerPhaseId,
  );
  const milestoneFromManifest = manifestMilestones.find(
    (m) => m.milestone_id === pointerMilestoneId,
  );

  const phaseLabel =
    pointerPhaseId == null
      ? null
      : pointer.phase?.id === pointerPhaseId
        ? labelFor(pointerPhaseId, pointer.phase?.display_name)
        : labelFor(pointerPhaseId, phaseFromManifest?.name);

  const milestoneLabel =
    pointerMilestoneId == null
      ? null
      : pointer.milestone?.id === pointerMilestoneId
        ? milestoneLabelForRun(pointerMilestoneId, pointer.milestone?.display_name, amSupplies)
        : milestoneLabelForRun(pointerMilestoneId, milestoneFromManifest?.name, amSupplies);

  // Pending Email Send (story 28633): everything ora does is finished; the one
  // open item is the AM sending the reply. The stage ora stopped on reads as
  // done, its last milestone as done, and ONE line after it — "Send the reply
  // — you" — is the current one. Milestones the history never showed keep
  // their state: nothing is claimed that did not run.
  const awaitingSend = status === "pending_email_send" && !stepExempt;
  const finalRows = awaitingSend ? withSendReplyStep(rows) : rows;

  return {
    source: "model",
    phaseLabel: stepExempt ? null : phaseLabel,
    milestoneLabel: stepExempt
      ? null
      : awaitingSend && finalRows.some((r) => r.current)
        ? SEND_REPLY_MILESTONE_LABEL
        : milestoneLabel,
    stepExempt,
    signInState: stepExempt
      ? resolveSignInState(exemptStepId, visited.phases.size > 0)
      : null,
    counter,
    rows: finalRows,
    legacyStepLabel: null,
  };
}

/** The line the stage view shows while a run waits for the AM to send the
 *  reply — the only thing left, and it is the AM's. */
export const SEND_REPLY_MILESTONE_ID = "send_reply_by_you";
export const SEND_REPLY_MILESTONE_LABEL = "Send the reply — you";

function withSendReplyStep(rows: PhaseRow[]): PhaseRow[] {
  const at = rows.findIndex((r) => r.current);
  if (at < 0) return rows;
  return rows.map((row, i) => {
    if (i !== at) return row;
    return {
      ...row,
      state: "completed",
      milestones: [
        ...row.milestones.map((m) =>
          m.current ? { ...m, state: "completed" as PhaseRowState, current: false } : m,
        ),
        {
          id: SEND_REPLY_MILESTONE_ID,
          label: SEND_REPLY_MILESTONE_LABEL,
          state: "hitl_waiting" as PhaseRowState,
          current: true,
        },
      ],
    };
  });
}

// ---------------------------------------------------------------------------
// readEvidenceDecision — Phase 3. The ONE reader of `evidence_decision`.
// ---------------------------------------------------------------------------

export interface EvidenceCardModel {
  decisionId: string | null;
  branch: "carrier_supplies" | "am_supplies";
  reason: string | null;
  presentation: EvidencePresentation;
  evidenceRequired: boolean;
  /**
   * Read from the ONE declared member `evidence_decision.evidence_attached`.
   * `true | false | null` — null means the server reported no attachment fact,
   * which is EVERY run today (AD-PLAN-10, Q3). Never coerced to false, and
   * never sourced from anywhere else: the `result` envelope is a separate
   * route, is meaningful only on a completed run, and this card renders on
   * non-terminal bodies only, so a result-sourced fallback would be dead code
   * masquerading as a data path.
   */
  evidenceAttached: boolean | null;
  /** The revert link — offered only while the carrier is still CAPABLE of
   *  posting the declarations and nothing is attached. */
  offerRevert: boolean;
  /** The carrier's documents arrived, so there is no branch left to take. */
  alreadyResumed: boolean;
  /** Verbatim, or null. A null renders the copy's carrier-neutral variant —
   *  "the carrier" is never invented as a name. */
  carrierName: string | null;
  /** Filename of the FIRST staged document, or null.
   *
   *  Kept because the add-in is tenant-installed and caches its bundle: a
   *  client running yesterday's code must keep rendering. Anything new should
   *  read `evidenceDocuments`. */
  evidenceFilename: string | null;
  /** Every staged document, in staging order.
   *
   *  `seq` is the HANDLE for removing one. A filename cannot be: the substrate
   *  de-dups staged documents on CONTENT, so two genuinely different files may
   *  share a name — which is exactly what an AM staging a corrected `dec.pdf`
   *  produces. `consumed` documents are already on the endorsement and the
   *  reply, so the remove control must not offer to take them back. */
  evidenceDocuments: StagedEvidenceDocument[];
  /** True once attach_carrier_documents (or the edoc attach) consumed the bytes. */
  evidenceConsumed: boolean;
}

/** One staged evidence document, as the substrate reports it. */
export interface StagedEvidenceDocument {
  seq: number;
  filename: string;
  consumed: boolean;
}

/**
 * Read `evidence_documents` off the decision envelope.
 *
 * Defensive per element rather than per array: a partially-shaped entry from an
 * older substrate must not cost the whole list, because a dropped document here
 * looks exactly like the bug this feature exists to fix.
 */
function readStagedDocuments(d: EvidenceDecisionSnapshot): StagedEvidenceDocument[] {
  const raw = d.evidence_documents;
  if (!Array.isArray(raw)) {
    // Older substrate: no per-document detail. Fall back to the names-only
    // list so the AM still SEES what is staged; without seq the remove control
    // simply is not offered, which is the honest degradation.
    const names = d.evidence_filenames;
    if (!Array.isArray(names)) return [];
    return names
      .filter((n): n is string => typeof n === "string" && n !== "")
      .map((filename, i) => ({ seq: i + 1, filename, consumed: false }));
  }
  return raw.flatMap((entry) => {
    if (typeof entry !== "object" || entry === null) return [];
    const e = entry as Record<string, unknown>;
    if (typeof e.seq !== "number" || typeof e.filename !== "string") return [];
    return [{ seq: e.seq, filename: e.filename, consumed: e.consumed === true }];
  });
}

function readCarrierName(snapshot: StreamSnapshot | null): string | null {
  // The launch payload is the only NON-TERMINAL place a carrier identity could
  // be declared today. `outputs.carrier_handoff.carrier_name` exists but is a
  // terminal-run output and can never reach this card. No story publishes a
  // carrier name on the running snapshot yet, so this is null on every real
  // run and the neutral copy renders — which is the honest outcome.
  const inputs = (snapshot as { inputs?: Record<string, unknown> } | null)
    ?.inputs;
  const raw = inputs?.["carrier_name"];
  return typeof raw === "string" && raw !== "" ? raw : null;
}

/**
 * Read the carrier-evidence decision, or null when the run has recorded no
 * branch. A run with no recorded branch renders NO card — never a card
 * guessing the carrier branch.
 *
 * Nothing here reads a STATUS literal to decide the branch (design Constraint
 * 15). `pickDrawerStatusFamily` gains no value and no new
 * `DrawerStatusFamily` exists, which is precisely why the card is a PROP on
 * each non-terminal body rather than a status family.
 */
export function readEvidenceDecision(
  snapshot: StreamSnapshot | null,
): EvidenceCardModel | null {
  const d = snapshot?.evidence_decision;
  if (d == null) return null;

  const branch = d.branch;
  if (branch !== "carrier_supplies" && branch !== "am_supplies") {
    // The object exists but names no declared branch — nothing to render, and
    // guessing one is the failure AC-3 forbids.
    return null;
  }

  const evidenceAttached =
    typeof d.evidence_attached === "boolean" ? d.evidence_attached : null;
  const reason = typeof d.reason === "string" ? d.reason : null;

  let presentation: EvidencePresentation;
  if (branch === "carrier_supplies") {
    presentation = "carrier_posts";
  } else if (evidenceAttached === true) {
    // "attached" is only ever a DECLARED server fact. `null` (unknown) resolves
    // to am_supplying, never to attached.
    presentation = "attached";
  } else if (reason === "carrier_does_not_post_edocs") {
    presentation = "carrier_does_not_post";
  } else {
    presentation = "am_supplying";
  }

  // The carrier is still CAPABLE of posting unless the run says it is not.
  const carrierStillPosts = reason !== "carrier_does_not_post_edocs";

  return {
    decisionId: typeof d.decision_id === "string" ? d.decision_id : null,
    branch,
    reason,
    presentation,
    evidenceRequired: d.evidence_required === true,
    evidenceAttached,
    // See the tracker's Phase 3 deviation note: the plan's literal gloss
    // (`presentation === "carrier_posts"`) would put "Wait for the carrier
    // instead" on the branch that is ALREADY waiting and remove it from the AM
    // branch, reversing the shipped `edoc-wait-manual-override` behaviour. The
    // probe's own title reads the condition as the CAPABILITY ("while the
    // carrier posts"), which is what this implements.
    offerRevert:
      branch === "am_supplies" && carrierStillPosts && evidenceAttached !== true,
    // Requires POSITIVE evidence that the carrier's documents landed. Status
    // alone cannot tell "resumed past the gate" from "override staged before
    // it" — both are `running` — and claiming the documents arrived without
    // evidence is exactly the honesty failure AC-3 forbids. So this is bound to
    // the same declared member as the `attached` presentation and lights up
    // when Q3 lands.
    alreadyResumed: branch === "carrier_supplies" && evidenceAttached === true,
    carrierName: readCarrierName(snapshot),
    evidenceFilename:
      typeof d.evidence_filename === "string" && d.evidence_filename
        ? d.evidence_filename
        : null,
    evidenceDocuments: readStagedDocuments(d),
    evidenceConsumed: d.evidence_consumed === true,
  };
}

/**
 * Early-override card for a v2 mortgagee run that has not yet recorded
 * `evidence_decision`. The design requires the skip-wait control on the
 * running surface before the evidence step; guessing a branch other than
 * "carrier posts / wait" would be the AC-3 failure. This is that wait
 * presentation, so the AM can take the override without waiting for the
 * server object.
 */
/**
 * Whether the carrier-evidence card belongs on the primary body right now.
 *
 * Early override is offered while the run is `running`. A competing HITL
 * (additional-interest picker, etc.) is not the evidence decision — hide the
 * wait card so it does not sit on top of "ora needs you" after that phase.
 * `pending_edocs` always shows it. Terminal runs do not.
 */
/**
 * Finishing-tail steps after the AM's skip-wait / stage action is done.
 * The card stays through Epic login and the carrier-action window so they
 * can still replace a file; once compose/attach starts it goes away.
 */
const PAST_CARRIER_EVIDENCE_STEPS = new Set([
  "compose_and_save_draft",
  "register_sent_reply_monitor",
  "additional_interest_check",
  "additional_interest_picker_gate",
  "additional_interest_apply",
  "additional_interest_delete",
  "additional_interest_relogin_gate",
  "set_status_submitted",
  "attach_edoc_declarations",
  "attach_carrier_documents",
  "add_closure_note",
  "close_pchg_activity",
  "service_summary_issued",
]);

export function shouldShowCarrierEvidenceCard(
  status: string | null | undefined,
  stepId: string | null | undefined,
  model: EvidenceCardModel | null,
): boolean {
  if (!model) return false;
  const s = (status ?? "").toLowerCase();
  // Parked for the AM's own documents: the card IS the uploader for that wait
  // in both hosts (story 28633), so nothing below may take it away.
  if (s === "pending_edocs" && model.branch === "am_supplies") return true;
  if (model.alreadyResumed) return false;
  if (model.evidenceConsumed) return false;
  if (
    s === "completed" ||
    s === "failed" ||
    s === "cancelled" ||
    s === "timeout_cancelled" ||
    s === "rejected"
  ) {
    return false;
  }
  if (s === "pending_email_send") return false;
  if (s === "pending_edocs") return true;
  if (s === "paused_hitl" || s === "warm_hitl" || s === "rpa_review") {
    // The drawer dropzone IS the carrier-action step — keep the card up
    // on those HITLs so staging there continues the run. Hide it on
    // unrelated gates (additional-interest picker, etc.).
    return (
      stepId === "edoc_wait" ||
      stepId === "carrier_action_instruction" ||
      stepId === "carrier_change_manual" ||
      stepId === "carrier_manual_change"
    );
  }
  // Once compose/attach/closeout starts, the skip-wait / stage card is
  // done — even if the poll dropped the filename after consume. Callers
  // must pass current_phase when step_id is absent (add-in GetStatus).
  if (stepId && PAST_CARRIER_EVIDENCE_STEPS.has(stepId)) {
    return false;
  }
  return true;
}

/** Live step id for evidence-card visibility. Add-in GetStatus maps the
 *  substrate pointer onto `current_phase`, not `step_id`. */
export function liveCarrierEvidenceStepId(
  snapshot: InstanceStatusSnapshot | null | undefined,
): string | null {
  if (!snapshot) return null;
  if (typeof snapshot.step_id === "string" && snapshot.step_id) {
    return snapshot.step_id;
  }
  if (typeof snapshot.current_phase === "string" && snapshot.current_phase) {
    return snapshot.current_phase;
  }
  const fromModel = snapshot.phase_model?.step?.id;
  return typeof fromModel === "string" && fromModel ? fromModel : null;
}

/**
 * The carrier-evidence card a run is entitled to right now, BEFORE the
 * visibility rule (`shouldShowCarrierEvidenceCard`) is applied: the run's own
 * recorded decision, or — for a v2 mortgagee run that has not recorded one yet
 * — the wait placeholder above.
 *
 * ONE implementation for BOTH hosts. Each drawer used to carry its own copy of
 * `recorded ?? (workflow_id === "carrier_mortgagee_update_v2" ? placeholder : null)`,
 * which is the add-in/ora parity rule inverted: the rule is that a decision
 * shared by both hosts lives in the shared module they already import, and
 * this one had ALREADY drifted once — the embedded workflow id had to be
 * updated independently in two files for the v2 rename (review finding #28).
 * The id itself is no longer embedded here either: it is the single declared
 * `V15_MORTGAGEE_WORKFLOW_ID`.
 *
 * The placeholder is built with a null carrier name deliberately: no
 * non-terminal snapshot publishes a carrier identity (see `readCarrierName`),
 * and the neutral copy is the honest rendering.
 */
export function carrierEvidenceCandidateFor(
  snapshot: StreamSnapshot | null | undefined,
): EvidenceCardModel | null {
  const metadata = readObj(
    (snapshot as { metadata?: unknown } | null | undefined)?.metadata,
  );
  const recorded = readEvidenceDecision(snapshot ?? null);
  // Story 28633: a run PARKED for the AM's own documents always has a card.
  // It is the ONE uploader for that wait in ora and the pane (the drawer
  // body's built-in uploader is gone). First, so no rule below can leave the AM
  // with nowhere to put the documents: not an older workflow that records no
  // decision, not documents handed over at the carrier step, and not a
  // recorded decision that still names the carrier (the question follows a
  // take-over; the recorded decision can lag it).
  if (isWaitingOnPerson(snapshot?.status, { hitlQuestion: snapshot?.hitl_question })) {
    return recorded?.branch === "am_supplies"
      ? recorded
      : placeholderAmDocumentsWaitCard(snapshot ?? null);
  }
  // Story 28633: the AM handed the documents over at the carrier upload step
  // (recorded on the run when they answered). The run attaches them without
  // ever parking for evidence, so the card would only invite the AM to attach
  // what they just attached — at any point in the run, recorded decision or not.
  if (Number(metadata?.carrier_documents_supplied) > 0) return null;
  if (recorded) return recorded;
  if ((snapshot?.workflow_id ?? "") !== V15_MORTGAGEE_WORKFLOW_ID) return null;
  // Story 28633: before the decision is recorded, the carrier's capability
  // (recorded on the run at Stage 2) already says who will supply the
  // documents. A carrier without eDocs gets its own card, inviting the AM to
  // attach the confirmation early, never "waiting for the carrier".
  const capability = readObj(metadata?.carrier_capability);
  return capability?.has_edocs === false
    ? placeholderCarrierDoesNotPostCard(null)
    : placeholderCarrierPostsCard(null);
}

/** The early card for a carrier that posts no eDocs: the AM's branch, the
 *  uploader open, nothing required yet (the run asks at the document step). */
export function placeholderCarrierDoesNotPostCard(
  carrierName: string | null,
): EvidenceCardModel {
  return {
    decisionId: null,
    branch: "am_supplies",
    reason: "carrier_does_not_post_edocs",
    presentation: "carrier_does_not_post",
    evidenceRequired: false,
    evidenceAttached: null,
    offerRevert: false,
    alreadyResumed: false,
    carrierName,
    evidenceFilename: null,
    evidenceDocuments: [],
    evidenceConsumed: false,
  };
}

/**
 * The card for a run parked waiting for the AM's own documents when no
 * AM-branch decision is recorded. Required: the run cannot finish without the
 * documents. The revert is offered only when the gate offers it
 * (`offer_wait_for_carrier`). Worded for a carrier without eDocs when the run
 * recorded that capability, else "you are attaching".
 */
function placeholderAmDocumentsWaitCard(snapshot: StreamSnapshot | null): EvidenceCardModel {
  const metadata = readObj((snapshot as { metadata?: unknown } | null)?.metadata);
  const noEdocs = readObj(metadata?.carrier_capability)?.has_edocs === false;
  return {
    decisionId: null,
    branch: "am_supplies",
    reason: noEdocs ? "carrier_does_not_post_edocs" : null,
    presentation: noEdocs ? "carrier_does_not_post" : "am_supplying",
    evidenceRequired: true,
    evidenceAttached: null,
    offerRevert: readObj(snapshot?.hitl_question)?.offer_wait_for_carrier === true,
    alreadyResumed: false,
    carrierName: readCarrierName(snapshot),
    evidenceFilename: null,
    evidenceDocuments: [],
    evidenceConsumed: false,
  };
}

export function placeholderCarrierPostsCard(
  carrierName: string | null,
): EvidenceCardModel {
  return {
    decisionId: null,
    branch: "carrier_supplies",
    reason: null,
    presentation: "carrier_posts",
    evidenceRequired: false,
    evidenceAttached: null,
    offerRevert: false,
    alreadyResumed: false,
    carrierName,
    evidenceFilename: null,
    evidenceDocuments: [],
    evidenceConsumed: false,
  };
}

// ---------------------------------------------------------------------------
// readChronology — Phase 5. The ONE reader of `chronology`.
// ---------------------------------------------------------------------------

export interface ChronologyRow {
  stepId: string | null;
  stepLabel: string | null;
  milestoneLabel: string | null;
  phaseLabel: string | null;
  exempt: boolean;
  startedAt: string | null;
  endedAt: string | null;
}

/**
 * The server's ordered history, in the SERVER's order.
 *
 * This function does not sort, does not group by phase, and never walks the
 * registered manifest: a manifest walk yields DECLARATION order, which diverges
 * from what happened the moment a run branches or retries — and that divergence
 * is exactly what someone opens the log to see.
 *
 * Returns `null` when the server reported no chronology and `[]` when it
 * reported an empty one. Those are two different facts and the log renders them
 * differently ("this run does not report a history" vs "no history recorded
 * yet"). NOTE: the plan's sketch typed this `ChronologyRow[]`, which would
 * collapse the two — the nullable return is what makes the plan's own
 * honest-states rule for this component expressible.
 */
export function readChronology(
  snapshot: StreamSnapshot | null,
): ChronologyRow[] | null {
  return toChronologyRows(snapshot?.chronology ?? null);
}

/**
 * The wire→row mapping on its own, for callers holding the chronology array
 * rather than the whole snapshot — the run-record page reads
 * `status.chronology` straight off its loader data and never has a
 * `StreamSnapshot`.
 *
 * Split out of `readChronology` (which now delegates) so `DetailedRunLog` has
 * exactly ONE upstream shape no matter which surface is feeding it. Preserves
 * the null/[] distinction, which both callers depend on.
 */
export function toChronologyRows(
  entries: ChronologyEntry[] | null | undefined,
): ChronologyRow[] | null {
  if (entries == null) return null;
  return entries.map((e) => ({
    stepId: e.step_id ?? null,
    stepLabel: e.step_display_name ?? null,
    milestoneLabel: e.milestone_display_name ?? null,
    phaseLabel: e.phase_display_name ?? null,
    // An entry the manifest does not declare is exempt in effect: it belongs to
    // no phase. Both signals are declared server data.
    exempt: e.exempt === true || (e.phase_id ?? null) === null,
    startedAt: e.started_at ?? null,
    endedAt: e.ended_at ?? null,
  }));
}
